/**
 * A sample generated paper, in the exact shape the pipeline emits.
 *
 * Content is drawn from chapter 12 "Binary" — the chapter spike 002 extracted —
 * and the style follows the profile spike 001 learned from a past paper, including the
 * `Shade` command word for multiple choice. It reconciles: 1 + 2 + 3 + 4 + 6 = 16.
 *
 * This stands in for the generation service until Phase 2 builds it.
 */

import type { Paper } from "../paper/types";

export const samplePaper: Paper = {
  title: "Practice Paper — Binary and Data Representation",
  duration_minutes: 30,
  total_marks: 16,
  instructions: [
    "Answer ALL questions.",
    "Write your answers in the spaces provided.",
    "You must not use a calculator.",
  ],
  questions: [
    {
      number: 1,
      topic: "Number systems",
      spec_ref: "3.4",
      command: "Shade",
      type: "multiple_choice",
      marks: 1,
      stem: "Shade one lozenge to show how many bits are in one byte.",
      options: ["4", "8", "16", "32"],
      answer: "B — 8",
      marking_points: ["B / 8 (1)"],
      parts: [],
    },
    {
      number: 2,
      topic: "Units of information",
      spec_ref: "3.4.1",
      command: "Define",
      type: "short_answer",
      marks: 2,
      stem: "A computer stores all data as binary digits.",
      options: [],
      answer: "",
      marking_points: [],
      parts: [
        {
          label: "(a)",
          text: "Define the term bit.",
          marks: 1,
          answer: "A single binary digit, either 0 or 1.",
          marking_points: ["A single binary digit / a 0 or a 1 (1)"],
        },
        {
          label: "(b)",
          text: "State how many bits are in one nibble.",
          marks: 1,
          answer: "Four.",
          marking_points: ["4 (1)"],
        },
      ],
    },
    {
      number: 3,
      topic: "Binary arithmetic",
      spec_ref: "3.4.3",
      command: "Calculate",
      type: "calculation",
      marks: 3,
      stem: "Calculate the denary value of the 8-bit binary number 0 1 0 1 1 0 1 0. You must show your working.",
      options: [],
      answer:
        "64 + 16 + 8 + 2 = 90. Place values 64, 16, 8 and 2 hold a 1; all other place values hold a 0.",
      marking_points: [
        "Correct place values identified (128 64 32 16 8 4 2 1) (1)",
        "Correct values selected: 64, 16, 8, 2 (1)",
        "Answer 90 (1)",
      ],
      parts: [],
    },
    {
      number: 4,
      topic: "Binary shifts",
      spec_ref: "3.4.4",
      command: "Complete",
      type: "code_trace",
      marks: 4,
      stem: "The binary number 0 0 0 1 1 0 0 0 is shifted left by two places. Complete the trace table to show the value after each shift, and state the effect on the denary value.",
      options: [],
      answer:
        "After one shift: 00110000 (48). After two shifts: 01100000 (96). Each left shift multiplies the denary value by 2, so the value is multiplied by 4.",
      marking_points: [
        "After first shift 00110000 (1)",
        "After second shift 01100000 (1)",
        "Denary value 96 (1)",
        "Each shift multiplies by 2 / overall multiplies by 4 (1)",
      ],
      parts: [],
    },
    {
      number: 5,
      topic: "Representing data",
      spec_ref: "3.4.6",
      command: "Explain",
      type: "extended",
      marks: 6,
      stem: "Explain why an image file that is stored at a higher colour depth produces a larger file, and describe one situation in which a lower colour depth would be an acceptable trade-off.",
      options: [],
      answer:
        "Colour depth is the number of bits used per pixel. Increasing it increases the bits stored for every pixel, and file size is the product of the pixel count and the bits per pixel, so the file grows proportionally. A lower colour depth is acceptable where the image has few distinct colours, such as a simple logo or diagram, or where the file must be transmitted over a slow connection and some loss of colour fidelity does not affect its purpose.",
      marking_points: [
        "Colour depth defined as bits per pixel (1)",
        "More bits per pixel means more data per pixel (1)",
        "File size = pixel count x bits per pixel (1)",
        "So file size increases proportionally with colour depth (1)",
        "A valid situation identified (e.g. logo, diagram, slow connection) (1)",
        "Justified with why the loss does not matter there (1)",
      ],
      parts: [],
    },
  ],
};
